import { IsBooleanString, IsOptional, IsUUID } from 'class-validator';

/**
 * Bulk intake — creating many enforcement files from one spreadsheet.
 *
 * Intake has always offered "Bulk import / automated system" as a method while
 * the portal admitted, on the form, that files were still created one at a
 * time. This is the batch path behind that option: a municipality exporting
 * from a 311 system or a spreadsheet of a summer's inspections gets one upload
 * instead of a hundred sittings at the intake form.
 *
 * It writes through the *same* services as single intake — a complaint, then a
 * file anchored to a local government — so every taxonomy check, geocode and
 * audit stamp that applies to a typed file applies to an imported one. A faster
 * door into the same room, not a second room.
 */

/** What the importer will do with one parsed row. */
export type ComplaintImportAction = 'create' | 'skip-duplicate';

/** The columns the importer reads. Everything else in the file is ignored. */
export const COMPLAINT_IMPORT_COLUMNS = [
  'address',
  'description',
  'complainant_name',
  'intake_method',
  'property_type',
  'priority',
  'priority_reason',
  'bylaw_category',
  'pan',
  'pid',
  'county',
  'owner_name',
  'owner_email',
  'owner_phone',
  'owner_locale',
  'mailing_address',
  'postal_code',
  'complainant_email',
  'complainant_phone',
  'complainant_relationship',
  'occupant_name',
  'notes',
] as const;

export type ComplaintImportColumn = (typeof COMPLAINT_IMPORT_COLUMNS)[number];

/**
 * Columns without which a row cannot become a file.
 *
 * `complainant_name` is required, and deliberately so. The column is
 * non-nullable on the complaint record, and an importer that quietly invented a
 * reporter — "Imported", or the officer running the upload — would be writing a
 * fabricated name onto a statutory record that a notice is later generated
 * from. A municipality importing from a system that genuinely has no reporter
 * per row can name the system itself; that is true, and a sentinel is not.
 */
export const COMPLAINT_IMPORT_REQUIRED_COLUMNS = [
  'address',
  'description',
  'complainant_name',
] as const satisfies readonly ComplaintImportColumn[];

/** One row, as the importer understood it. */
export interface ComplaintImportRow {
  /** 1-based line in the uploaded file; the header is row 1. */
  rowNumber: number;
  address: string;
  description: string;
  /** Resolved method — the row's own, or the batch default. */
  intakeMethod: string;
  priority: string;
  ownerName: string | null;
  action: ComplaintImportAction;
  /** Why the action is what it is. Shown beside the row in the preview. */
  message: string | null;
  /** Set once committed, so the officer can open what was created. */
  fileId: string | null;
  /** The file number assigned on commit. Null on a dry run. */
  reference: string | null;
}

/**
 * A row that will not be imported.
 *
 * Separate from {@link ComplaintImportRow} because these need a different
 * answer from the officer: a duplicate is a decision, an error is a correction
 * to make in the spreadsheet and upload again.
 */
export interface ComplaintImportRowError {
  rowNumber: number;
  /** The offending column, where the failure belongs to one. */
  column: string | null;
  message: string;
}

export interface ComplaintImportResult {
  /** True when nothing was written — the preview pass. */
  dryRun: boolean;
  /** The local government every imported file was anchored to. */
  localGovernmentId: string;
  localGovernmentName: string | null;
  /** Columns present in the upload that the importer does not read. */
  ignoredColumns: string[];
  rows: ComplaintImportRow[];
  rowErrors: ComplaintImportRowError[];
  /** Files actually created. Always 0 on a dry run. */
  created: number;
  /** Rows skipped because an open file already covers that address. */
  skipped: number;
  /**
   * A whole-file failure — an unparseable CSV, a missing required column.
   *
   * Distinct from `rowErrors`: nothing was read, so there is no per-row detail
   * to show and the officer needs one sentence about the file itself.
   */
  fileError: string | null;
}

/**
 * Sent as **query parameters**, not in the multipart body.
 *
 * Nest runs guards before interceptors, so the scope guard that authorises the
 * upload reads `localGovernmentId` before Multer has parsed the body. In the
 * body it is invisible at that moment and every upload is refused; in the query
 * it is there. None of these three is personal data.
 */
export class ImportComplaintsDto {
  /**
   * The local government every file in the batch is anchored to.
   *
   * One per upload rather than a column: the anchor is the tenant boundary, the
   * caller's authority to create files is checked against it, and a spreadsheet
   * that could scatter files across municipalities would need that check per
   * row with no way to show the officer the result before it happened.
   */
  @IsUUID()
  localGovernmentId!: string;

  /** Route every created file to one officer. Same gate as single intake. */
  @IsOptional()
  @IsUUID()
  assignedOfficerId?: string;

  /**
   * Query values are all strings, so this is a string flag rather than a
   * boolean: 'true' parses, validates and reports without writing anything.
   */
  @IsOptional()
  @IsBooleanString()
  dryRun?: string;
}

/**
 * A starter CSV, built from the column list the server validates against.
 *
 * Generated rather than kept as a static asset so the two cannot drift: adding
 * a column to {@link COMPLAINT_IMPORT_COLUMNS} puts it in the template on the
 * next build, instead of leaving officers with a file the importer has outgrown.
 *
 * Lives in the contract rather than in either app because both offer it — the
 * portal as a download, the mobile app through the share sheet — and a second
 * copy of the generator is a second way for the template to stop matching.
 *
 * The single example row is filled only in the required columns, so what an
 * officer sees on opening it is the minimum a row actually needs.
 */
export function buildComplaintImportTemplateCsv(): string {
  const example: Partial<Record<ComplaintImportColumn, string>> = {
    address: '14 Example Street, Miramichi NB',
    description: 'Accumulation of scrap material in the rear yard.',
    complainant_name: '311 System',
  };
  const header = COMPLAINT_IMPORT_COLUMNS.join(',');
  const row = COMPLAINT_IMPORT_COLUMNS.map((column) => {
    const value = example[column] ?? '';
    // Quote anything containing a comma, or the example row will not parse.
    return value.includes(',') ? `"${value}"` : value;
  }).join(',');
  return `${header}\n${row}\n`;
}
